import { createEffect, createMemo, createSignal, For, onCleanup, Show, } from 'solid-js'
import { useKeyframeTarget } from '@/contexts/KeyframeTargetContext'
import { useTimeline } from '@/contexts/TimelineContext'
import { TIMELINE_PARAMETERS } from '@/utils/timeline'
import { CurveEditor } from './CurveEditor/CurveEditor'
import ui from './DopeSheet.module.css'
import { useScrollSync } from './hooks/useScrollSync'
import { useSeekScrubber } from './hooks/useSeekScrubber'
import { useTrackNameWidth } from './hooks/useTrackNameWidth'
import { useZoomGestures } from './hooks/useZoomGestures'
import { KeyframeContextMenu } from './KeyframeContextMenu'
import { TrackContextMenu } from './TrackContextMenu'

/**
 * Roots of every built-in animatable parameter path (e.g. `camera`, `camera3D`,
 * `finalTransform`, `vibrancy`, `blendWeight`). Derived from the authoritative
 * parameter list so it can't drift — anything else with a `<tid>.<vid>` shape is
 * treated as a transform/variation target and validated against the flame.
 */
const STATIC_PARAM_ROOTS = new Set(
  TIMELINE_PARAMETERS.map((p) => p.path.split('.')[0]!),
)

function pathLabel(path: string): string {
  return path
    .split('.')
    .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
    .join(' › ')
}

const BASE_FRAME_WIDTH = 24
const BASE_TRACK_HEIGHT = 20

import { DopeSheetGrid } from './DopeSheetGrid'
import { KeyframeInspector } from './KeyframeInspector'
import type { FlameDescriptor, TransformId, VariationId, } from '@/flame/schema/flameSchema'

/** Zoom controls the dope sheet hands up to the timeline header (the buttons
 *  render there, but the state lives here with the scroll/ruler refs). */
export interface DopeSheetViewApi {
  zoomLevel: () => number
  setZoomLevel: (v: number) => void
  autoFitZoom: () => void
}

export interface DopeSheetProps {
  formatTrackLabel?: (path: string) => string
  flameDescriptor?: FlameDescriptor
  /** Seek the playhead to a keyframe when it's selected (header toggle). */
  seekOnSelect?: boolean
  /** Show the value-curve editor for the selected parameter (header toggle). */
  showCurve?: boolean
  /** Called on mount with the zoom API consumed by the header's View group. */
  registerViewApi?: (api: DopeSheetViewApi | undefined) => void
}

export function DopeSheet(props: DopeSheetProps) {
  const timeline = useTimeline()!
  const { setTargetedParameter, setSelectedKeyframePath } = useKeyframeTarget()

  const totalFrames = () =>
    timeline.config().endFrame - timeline.config().startFrame

  // Horizontal scroll of the tracks, mirrored onto the curve graph so it stays
  // pixel-aligned with the diamonds.
  const [scrollLeft, setScrollLeft] = createSignal(0)

  const curveLabel = () => {
    const p = selectedTrack()
    if (!p) return null
    return props.formatTrackLabel ? props.formatTrackLabel(p) : pathLabel(p)
  }
  let containerRef: HTMLDivElement | undefined
  let tracksScrollRef!: HTMLDivElement
  let seekRulerRef!: HTMLDivElement
  let seekLaneRef!: HTMLDivElement | undefined

  const trackNameWidth = useTrackNameWidth()

  const { zoomLevel, setZoomLevel, frameWidth, trackHeight, autoFitZoom } =
    useZoomGestures(
      () => containerRef,
      () => seekRulerRef,
      () => tracksScrollRef,
      () => seekLaneRef,
      totalFrames,
      BASE_FRAME_WIDTH,
      BASE_TRACK_HEIGHT,
      trackNameWidth,
    )

  props.registerViewApi?.({ zoomLevel, setZoomLevel, autoFitZoom })
  onCleanup(() => props.registerViewApi?.(undefined))

  useScrollSync(
    () => tracksScrollRef,
    () => seekLaneRef,
  )

  const laneWidth = () => totalFrames() * frameWidth()

  const [selectedKeyframe, setSelectedKeyframe] = createSignal<{
    path: string
    frame: number
  } | null>(null)
  // The "current" track: set by clicking a lane/name OR a keyframe. Drives
  // the curve editor's graphed parameter and keyboard targeting (I inserts),
  // so a parameter can be inspected without hunting for a diamond to click.
  const [selectedTrack, setSelectedTrack] = createSignal<string | null>(null)

  function selectKeyframe(path: string, frame: number) {
    setSelectedTrack(path)
    setSelectedKeyframe({ path, frame })
  }

  /** Lane/name click: make the track current without selecting a keyframe
   *  (the inspector row stays hidden; an open curve editor retargets). */
  function selectTrack(path: string) {
    setSelectedTrack(path)
    setSelectedKeyframe(null)
  }

  createEffect(() => {
    const added = timeline.lastAddedKeyframe()
    if (added) {
      selectKeyframe(added.path, added.frame)
    }
  })

  // Drop a selection whose track was removed (track context menu, orphan
  // cleanup, loading another flame) so the curve editor can't graph a ghost.
  createEffect(() => {
    const path = selectedTrack()
    if (path && !timeline.tracks().some((t) => t.parameterPath === path)) {
      setSelectedTrack(null)
      setSelectedKeyframe(null)
    }
  })

  const [contextMenu, setContextMenu] = createSignal<{
    x: number
    y: number
    path: string
    frame: number
  } | null>(null)

  const [trackContextMenu, setTrackContextMenu] = createSignal<{
    x: number
    y: number
    path: string
  } | null>(null)

  const { handleSeekPointerDown } = useSeekScrubber(frameWidth)

  function handleContextMenu(e: MouseEvent, path: string, frame: number) {
    setContextMenu({ x: e.clientX, y: e.clientY, path, frame })
  }

  function handleTrackContextMenu(e: MouseEvent, path: string) {
    setTrackContextMenu({ x: e.clientX, y: e.clientY, path })
  }

  function handleDragKeyframe(
    path: string,
    oldFrame: number,
    newFrame: number,
  ) {
    timeline.moveKeyframe(path, oldFrame, newFrame)
    selectKeyframe(path, newFrame)
  }

  // Target the current TRACK (not just a clicked keyframe): picking a lane is
  // enough to aim keyboard inserts (I) and the sidebar highlight at it.
  createEffect(() => {
    const path = selectedTrack()
    setTargetedParameter(path)
    setSelectedKeyframePath(path)
  })

  const activeTracks = createMemo(() => {
    const all = timeline.tracks()
    const fmt = props.formatTrackLabel
    const flame = props.flameDescriptor

    return all
      .map((t) => {
        let isOrphaned = false
        const parts = t.parameterPath.split('.')
        if (flame) {
          if (parts[0] === 'transform' && parts.length >= 2) {
            // Brand the id parsed out of the parameterPath so it can index the
            // TransformId-keyed transforms record.
            const tid = parts[1]! as TransformId
            // Handle global transform.color.x vs transform.<tid>.probability
            if (tid !== 'color' && !flame.transforms[tid]) {
              isOrphaned = true
            }
          } else if (STATIC_PARAM_ROOTS.has(parts[0]!)) {
            // A known built-in parameter (camera, finalTransform, render
            // settings, blendWeight, …) — always valid.
            isOrphaned = false
          } else if (parts.length >= 2) {
            // It's likely <tid>.<vid> or <tid>.<vid>.<param>; brand the ids
            // parsed out of the path so they can index the branded records.
            const tid = parts[0]! as TransformId
            const vid = parts[1]! as VariationId
            if (
              !flame.transforms[tid] ||
              !flame.transforms[tid].variations[vid]
            ) {
              isOrphaned = true
            }
          }
        }

        return {
          path: t.parameterPath,
          label: fmt ? fmt(t.parameterPath) : pathLabel(t.parameterPath),
          isOrphaned,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  })

  const frameLabels = createMemo(() => {
    const start = timeline.config().startFrame
    const end = timeline.config().endFrame
    const labels: number[] = []
    const minPxBetweenLabels = 50
    const step = Math.max(1, Math.ceil(minPxBetweenLabels / frameWidth()))
    for (let f = start; f <= end; f += step) {
      labels.push(f)
    }
    // Always include the end frame so the full range is visible
    if (labels[labels.length - 1] !== end) {
      labels.push(end)
    }
    return labels
  })

  const currentFrame = () => timeline.currentFrame()

  return (
    <div class={ui.container} ref={containerRef} data-tour-target="dope-sheet">
      {/* ── Keyframe inspector (only while a keyframe is selected) ── */}
      <Show when={selectedKeyframe()}>
        <KeyframeInspector selectedKeyframe={selectedKeyframe()} />
      </Show>

      {/* ── Curve editor (selected track) ── */}
      <Show when={props.showCurve}>
        <CurveEditor
          path={selectedTrack()}
          label={curveLabel()}
          selectedFrame={selectedKeyframe()?.frame ?? null}
          onSelectKeyframe={selectKeyframe}
          onContextMenu={handleContextMenu}
          frameWidth={frameWidth()}
          startFrame={timeline.config().startFrame}
          endFrame={timeline.config().endFrame}
          trackNameWidth={trackNameWidth()}
          scrollLeft={scrollLeft()}
        />
      </Show>

      {/* ── Seek ruler ── */}
      <div class={ui.seekRuler} ref={seekRulerRef}>
        <div
          style={{
            width: `${trackNameWidth()}px`,
            'flex-shrink': '0',
          }}
        />
        <div
          ref={seekLaneRef}
          class={ui.seekRulerLane}
          data-tour-target="seek-ruler"
          style={{ 'min-width': `${laneWidth()}px` }}
          onPointerDown={handleSeekPointerDown}
        >
          <For each={frameLabels()}>
            {(frame) => (
              <span
                class={ui.seekLabel}
                style={{
                  left: `${(frame - timeline.config().startFrame) * frameWidth()}px`,
                  transform:
                    frame === timeline.config().startFrame
                      ? 'translateX(0)'
                      : undefined,
                }}
              >
                {frame}
              </span>
            )}
          </For>
          {/* Seek playhead indicator */}
          <div
            class={ui.seekPlayhead}
            style={{
              left: `${(currentFrame() - timeline.config().startFrame) * frameWidth()}px`,
            }}
          >
            <div class={ui.seekPlayheadHead} />
            <div class={ui.seekPlayheadLine} />
          </div>
        </div>
      </div>

      {/* ── Tracks ── */}
      <DopeSheetGrid
        tracksScrollRef={(el) => (tracksScrollRef = el)}
        onScroll={(e) => {
          if (seekLaneRef) {
            seekLaneRef.scrollLeft = e.currentTarget.scrollLeft
          }
          setScrollLeft(e.currentTarget.scrollLeft)
        }}
        activeTracks={activeTracks()}
        frameWidth={frameWidth()}
        trackHeight={trackHeight()}
        startFrame={timeline.config().startFrame}
        endFrame={timeline.config().endFrame}
        currentFrame={currentFrame()}
        selectedKeyframe={selectedKeyframe()}
        selectedTrack={selectedTrack()}
        onSelectKeyframe={(path, frame) => {
          selectKeyframe(path, frame)
          if (props.seekOnSelect && frame !== currentFrame()) {
            timeline.goToFrame(frame)
          }
        }}
        onSelectTrack={selectTrack}
        onDragKeyframe={handleDragKeyframe}
        onContextMenu={handleContextMenu}
        onTrackContextMenu={handleTrackContextMenu}
        trackNameWidth={trackNameWidth()}
      />

      {/* ── Context menu ── */}
      <Show when={contextMenu()}>
        {(cm) => (
          <KeyframeContextMenu
            x={cm().x}
            y={cm().y}
            parameterPath={cm().path}
            frame={cm().frame}
            onClose={() => setContextMenu(null)}
          />
        )}
      </Show>

      <Show when={trackContextMenu()}>
        {(cm) => {
          const track = activeTracks().find((t) => t.path === cm().path)
          const isOrphaned = track?.isOrphaned ?? false
          return (
            <TrackContextMenu
              x={cm().x}
              y={cm().y}
              parameterPath={cm().path}
              isOrphaned={isOrphaned}
              onClose={() => setTrackContextMenu(null)}
              onClearAllInvalid={() => {
                const orphanedPaths = activeTracks()
                  .filter((t) => t.isOrphaned)
                  .map((t) => t.path)
                timeline.removeTracks(orphanedPaths)
              }}
            />
          )
        }}
      </Show>
    </div>
  )
}
